/**
 * Per-artist visual theming — generates a deterministic, distinct color
 * gradient for every artist based on a hash of their name. Same artist
 * always gets the same palette across the app; different artists never
 * look like the same gradient.
 *
 *   themeFor("Drake")  -> { from: "#FF2E00", to: "#7C5CFF", textOn: "#fff" }
 *
 * Used by:
 *   - <ArtistCard>  for the avatar fallback gradient
 *   - <Artist>      hero background tint
 *   - Home Trending Artists row
 *   - Anywhere else an artist needs visual identity
 */

// Curated palette of 14 distinct color pairs. Designed for the new
// brutalist/editorial dark theme: each pair leans bold-but-not-pastel, never
// duplicates the brand vermilion (#FF2E00 — reserved for accents/CTAs), and
// works against #050505. Mix of warm/cool/neon families with high saturation.
const PALETTE = [
  { from: "#FF2E00", to: "#7A0010" },  // vermilion → blood
  { from: "#FF6B35", to: "#F7C548" },  // tangerine → gold
  { from: "#5E8BFF", to: "#B580FF" },  // sky → orchid
  { from: "#E2FF31", to: "#5BBE7B" },  // lime → emerald
  { from: "#FF5252", to: "#FFB300" },  // crimson → amber
  { from: "#22D3EE", to: "#A78BFA" },  // cyan → lavender
  { from: "#F472B6", to: "#FB923C" },  // rose → coral
  { from: "#10B981", to: "#3B82F6" },  // teal → blue
  { from: "#EC4899", to: "#8B5CF6" },  // magenta → indigo
  { from: "#FBBF24", to: "#EF4444" },  // gold → red
  { from: "#6366F1", to: "#06B6D4" },  // electric indigo → cyan
  { from: "#A855F7", to: "#FF2E00" },  // purple → vermilion
  { from: "#84CC16", to: "#0EA5E9" },  // lime → ocean
  { from: "#F97316", to: "#DB2777" },  // orange → fuchsia
];

/** Cheap, stable string hash (djb2 variant). Same input → same output. */
function hashString(s) {
  let h = 5381;
  const str = String(s || "anon");
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(h);
}

/**
 * Get the gradient theme for an artist. ``key`` is usually the artist's
 * displayable name; for iTunes artists we fall back to the numeric ID
 * so unknown-name catalog rows still look distinct.
 */
export function themeForArtist(key) {
  const idx = hashString(key) % PALETTE.length;
  return PALETTE[idx];
}

/** Convenience CSS string for ``style={{ background: ... }}``. */
export function gradientCssForArtist(key, direction = "135deg") {
  const t = themeForArtist(key);
  return `linear-gradient(${direction}, ${t.from} 0%, ${t.to} 100%)`;
}

/** Soft glow variant — for hero backgrounds (low-alpha radial). */
export function heroGlowForArtist(key) {
  const t = themeForArtist(key);
  return (
    `radial-gradient(900px circle at 30% 20%, ${t.from}40, transparent 60%), ` +
    `radial-gradient(700px circle at 80% 80%, ${t.to}30, transparent 60%), #050505`
  );
}
